import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { useOnError } from 'src/utils/errors';
import { ICON_MAP } from 'src/utils/constants';
import { GeneralASError } from 'src/registry/types/axios';
import { cn } from 'src/utils';
import { TaxReason } from 'src/features/taxReasons/utils/types';
import { taxReasonFormSchema, TaxReasonFormSchemaValues } from 'src/features/taxReasons/utils/formSchema';
import { useGetTaxReason } from 'src/features/taxReasons/api/useGetTaxReason';
import TaxReasonForm from 'src/features/taxReasons/components/forms/TaxReasonForm';

interface TaxReasonFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedTaxReason?: TaxReason;
    children?: React.ReactNode;
    isOpen?: boolean;
    isPending: boolean;
    icon: IconProp;
    title: string;
    subtitle: string;
    mutate: (
        data: TaxReasonFormSchemaValues,
        onSuccess: () => void,
        onError: (error: GeneralASError) => void,
    ) => void;
}

function TaxReasonFormModal<TData>({
    selectedTaxReason,
    isPending,
    children,
    className,
    icon,
    title,
    subtitle,
    mutate,
}: TaxReasonFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);

    // We are in edit mode if we have a selected service provider
    // otherwise it means we are creating a new service provider

    const { data: taxReason } = useGetTaxReason({
        id: selectedTaxReason?.id ?? '',
        // We only want to fetch if we're in edit
        config: { enabled: !!selectedTaxReason?.id && isModalOpen },
    });
    const onError = useOnError();

    const defaultValues = React.useMemo(
        () => {
            return {
                name: taxReason?.name ?? '',
                value: taxReason?.value
                    ? (Number(taxReason?.value) * 100).toFixed(2)
                    : '',
                is_active: taxReason ? taxReason?.is_active ?? false :  undefined,
                is_default: taxReason ? taxReason?.is_default ?? false : undefined,
            }
        },
        [taxReason],
    );

    const form = useForm<TaxReasonFormSchemaValues>({
        resolver: zodResolver(taxReasonFormSchema),
        defaultValues,
    });

    // defaultValues are cached and require the reset API to update
    React.useEffect(() => {
        form.reset(defaultValues);
    }, [form, isModalOpen, defaultValues]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();
        setIsModalOpen(isOpen);
    };

    const onSubmit = React.useCallback(
        (values: TaxReasonFormSchemaValues) => {
            const numericPercent = parseFloat(values.value ?? '');
            const taxReasonValue =
                !isNaN(numericPercent) && values.value !== '' ? (numericPercent / 100).toFixed(4) : '';

            const payload = {
                ...values,
                value: taxReasonValue,
            };
            mutate(
                { ...payload },
                () => setIsModalOpen(false),
                (error) => onError(error, form.setError),
            );
        },
        [mutate, onError, form.setError],
    );

    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children ? (
                <DialogTrigger asChild>{children}</DialogTrigger>
            ) : (
                <DialogTrigger asChild>
                    <Button className={cn('ml-auto', className)}>
                        <FontAwesomeIcon icon={ICON_MAP.ADD} />
                        {title}
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={icon} />
                        <div>
                            <DialogTitle>{title}</DialogTitle>
                            <DialogDescription>{subtitle}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <TaxReasonForm form={form} onSubmit={onSubmit} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending || (selectedTaxReason && !form.formState.isDirty)}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {title}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default TaxReasonFormModal;
